import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { ActiveCueGroup, cues, CueType } from '../../StudioCues/Cue';
import StudioCueWrapper from '../../StudioCues/StudioCueWrapper';
import CuesToolbarCTA from '../../CallToAction/CuesToolbarCTA';
import GlobalTheme from '../../../theme';
import { useAppDispatch } from '../../../store/hooks';
import { updateCue } from '../../../store/CuesSlice';

export interface StudioCuesOptionsProps {
  value: ActiveCueGroup;
}

const useStyles = makeStyles()(theme => ({
  bottomDivider: {
    borderBottom: `1px solid ${theme.palette.grey[900]}`,
  },
  cueGroupDescription: {
    minHeight: theme.spacing(5),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'relative',
  },
  cueGroup: {
    backgroundColor: palette.gray_1100,
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
    padding: theme.spacing(3, 2.5),
    position: 'relative',
    gap: theme.spacing(2),
  },
  activeCueGroup: {
    backgroundColor: palette.gray_1000,
  },
  cueActions: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'flex-start',
    padding: 0,
  },
  reset: {
    padding: 0,
    minWidth: 'unset',
  },
}));

const StudioCuesOptions = ({ value }: StudioCuesOptionsProps) => {
  const { classes, cx } = useStyles();
  const [verbalCuesAreActive, nonVerbaleCuesAreActive] = useMemo(
    () => [value === ActiveCueGroup.word, value === ActiveCueGroup.pause],
    [value]
  );
  const dispatch = useAppDispatch();

  const onResetVerbalCues = useCallback(() => {
    dispatch(
      updateCue({
        items: [
          { cueType: CueType.loudness, value: 0 },
          { cueType: CueType.tempo, value: 0 },
          { cueType: CueType.pitch, value: 0 },
        ],
      })
    );
  }, [dispatch]);

  const onResetNonVerbalCues = useCallback(() => {
    dispatch(
      updateCue({
        items: [{ cueType: CueType.pause, value: 0 }],
      })
    );
  }, [dispatch]);

  const [verbalCues, nonVerbalCues] = useMemo(
    () => [
      cues.filter(cue => cue.group === ActiveCueGroup.word),
      cues.filter(cue => cue.group === ActiveCueGroup.pause),
    ],
    []
  );

  return (
    <Box>
      <CuesToolbarCTA />
      <Box
        className={cx(
          classes.cueGroup,
          ActiveCueGroup.word === value && classes.activeCueGroup
        )}
        height={GlobalTheme.spacing(43)}
      >
        <Box className={classes.cueGroupDescription}>
          <Box paddingBottom={1}>
            <Typography variant="h5" bold>
              Verbal cues
            </Typography>
          </Box>
          <Typography variant="pSm" color="secondary">
            Select a word to adjust these cues.
          </Typography>
          <Box className={classes.cueActions}>
            <Button
              variant="text"
              tabIndex={0}
              className={classes.reset}
              onClick={onResetVerbalCues}
              color="primary"
              disabled={!verbalCuesAreActive}
            >
              <Icon type="replay" />
            </Button>
          </Box>
        </Box>
        {verbalCues.map(cue => (
          <StudioCueWrapper
            key={cue.type}
            type={cue.type}
            label={cue.label}
            SliderProps={cue.SliderProps}
            isActive={verbalCuesAreActive}
          />
        ))}
      </Box>
      <Box
        className={cx(
          classes.cueGroup,
          ActiveCueGroup.pause === value && classes.activeCueGroup
        )}
        height={GlobalTheme.spacing(22)}
        borderTop={`1px solid ${palette.gray_900}`}
      >
        <Box className={classes.cueGroupDescription}>
          <Box paddingBottom={1}>
            <Typography variant="h5" bold>
              Non-verbal cues
            </Typography>
          </Box>
          <Typography variant="pSm" color="secondary">
            Select punctuation to adjust these cues.
          </Typography>
          <Box className={classes.cueActions}>
            <Button
              variant="text"
              tabIndex={0}
              className={classes.reset}
              onClick={onResetNonVerbalCues}
              color="primary"
              disabled={!nonVerbaleCuesAreActive}
            >
              <Icon type="replay" />
            </Button>
          </Box>
        </Box>
        {nonVerbalCues.map(cue => (
          <StudioCueWrapper
            key={cue.type}
            type={cue.type}
            label={cue.label}
            SliderProps={cue.SliderProps}
            isActive={nonVerbaleCuesAreActive}
          />
        ))}
      </Box>
    </Box>
  );
};

export default StudioCuesOptions;
